import { useState } from 'react';

import { Input } from 'components/input';
import { formatPrice } from 'utils/helpers';
import { Button, ButtonTypes } from 'components/button';
import {
  Event,
  useSaveMissingSaleMutation,
  useDeleteMissingSaleMutation,
} from 'store/api/event';

import * as S from './styles';
import {
  Tender,
  SaleKind,
  MissingSale,
  MissingCard,
  totalAmount,
  tenderLabels,
  MissingSaleInput,
  missingSaleDateError,
  missingSaleDuplicateId,
  missingSaleErrorMessage as errorMessage,
} from './types';
// The event-rate tax on a pre-tax amount, as Main computes it
// (clix-shared missingSales: round(gross cents x rate / 100)).
const eventRateTax = (gross: unknown, rate: unknown) =>
  (Math.round(Number(gross || 0) * Number(rate || 0)) / 100).toFixed(2);
const cents = (value: unknown) => Math.round(Number(value || 0) * 100);
// Whole days a sale date lies before the event's first or after its last day.
const daysOutsideEvent = (saleDate: string | undefined, event: Event) => {
  const saleDay = Date.parse(`${saleDate}T00:00:00Z`);
  return Math.max(
    (Date.parse(`${event.startDate?.slice(0, 10)}T00:00:00Z`) - saleDay) /
      86400000,
    (saleDay -
      Date.parse(
        `${(event.endDate || event.startDate)?.slice(0, 10)}T00:00:00Z`
      )) /
      86400000,
    0
  );
};

export const MissingSaleForm = ({
  row,
  event,
  onClose,
  original,
  onDuplicate,
  kind = 'sale',
  remove = false,
  originalSaleDate,
}: {
  event: Event;
  kind?: SaleKind;
  remove?: boolean;
  row?: MissingSale;
  onClose: () => void;
  original?: MissingSale;
  originalSaleDate?: string;
  onDuplicate?: (id: number) => void;
}) => {
  const [save, { isLoading: saving }] = useSaveMissingSaleMutation();
  const [destroy, { isLoading: deleting }] = useDeleteMissingSaleMutation();
  const [form, setForm] = useState<MissingSaleInput>(() =>
    row
      ? {
          ...row,
          reason: '',
          dateOverride: daysOutsideEvent(row.saleDate, event) > 1,
          // A row saved with a tax override or a date outside the event was
          // confirmed when it was saved: it opens with those boxes ticked,
          // so saving it again does not ask again.
          taxOverride:
            cents(row.taxAmount) !==
            cents(eventRateTax(row.grossAmount, event.tax)),
        }
      : {
          kind,
          note: '',
          reason: '',
          sourceRef: '',
          taxAmount: '0',
          tipAmount: '0',
          grossAmount: '0',
          cogsAmount: null,
          settlementKey: '',
          customerFeeAmount: '0',
          tender: original?.tender || 'cash',
          originalMissingSaleId: original?.id || null,
          processorRefs: original?.processorRefs || {},
          chargebackOutcome: kind === 'chargeback' ? 'open' : null,
          saleDate: original?.saleDate || event.startDate?.slice(0, 10),
        }
  );
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [duplicateId, setDuplicateId] = useState<number>();
  const change = (key: keyof MissingSaleInput, value: any) =>
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'grossAmount' && !current.taxOverride) {
        next.taxAmount = eventRateTax(value, event.tax);
      }
      if (
        key === 'tender' &&
        !['clover_card', 'payanywhere_card'].includes(value)
      ) {
        next.customerFeeAmount = '0';
        next.settlementKey = '';
        next.processorRefs = {};
      }
      return next;
    });
  const expectedTax = eventRateTax(form.grossAmount, event.tax);
  const taxDiffers = cents(form.taxAmount) !== cents(expectedTax);
  const outsideDays = daysOutsideEvent(form.saleDate, event);
  const dateError = missingSaleDateError({
    outsideDays,
    kind: form.kind,
    saleDate: form.saleDate,
    originalSaleDate: original?.saleDate || originalSaleDate,
  });
  const caseOutcome = !!form.settlementKey?.match(/^fiserv:chargeback:\d+$/);
  const card = ['clover_card', 'payanywhere_card'].includes(form.tender || '');
  const cards = form.processorRefs?.cards || [];
  const setCards = (value: MissingCard[]) =>
    change('processorRefs', { ...form.processorRefs, cards: value });
  const changeCard = (i: number, key: keyof MissingCard, value: any) =>
    setCards(
      cards.map((c, index) => (index === i ? { ...c, [key]: value } : c))
    );
  const field = (key: keyof MissingSaleInput, label: string, type = 'text') => (
    <Input
      key={key}
      type={type}
      label={label}
      aria-label={label}
      name={`missing-${key}`}
      value={String(form[key] ?? '')}
      min={type === 'number' ? '0' : undefined}
      step={type === 'number' ? '0.01' : undefined}
      onChange={(e) => change(key, e.target.value)}
      required={['reason', 'saleDate', 'sourceRef'].includes(key)}
    />
  );
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (remove) {
        await destroy({
          rowId: row!.id,
          eventId: event.id,
          reason: form.reason,
          version: row!.version,
        }).unwrap();
      } else {
        await save({ data: form, rowId: row?.id, eventId: event.id }).unwrap();
      }
      onClose();
    } catch (err: any) {
      setError(errorMessage(err));
      if (err?.status === 409) {
        const duplicate = missingSaleDuplicateId(err);
        if (duplicate) {
          setDuplicateId(duplicate);
        } else {
          setConflict(true);
        }
      }
    }
  };
  return (
    <S.Form onSubmit={submit}>
      {remove ? (
        <p>
          Delete this {tenderLabels[row!.tender]} record for {row!.saleDate}? It
          stays in history.
        </p>
      ) : (
        <>
          <S.Fields>
            {field('saleDate', 'Sale date', 'date')}
            <S.SelectLabel>
              Tender
              <select
                value={form.tender}
                disabled={!!original}
                onChange={(e) => change('tender', e.target.value as Tender)}
              >
                {Object.entries(tenderLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </S.SelectLabel>
            {field('grossAmount', 'Sales (pre-tax)', 'number')}
            {field('taxAmount', 'Tax', 'number')}
            {field('tipAmount', 'Tip', 'number')}
            {card && field('customerFeeAmount', 'Customer fee', 'number')}
            {field(
              'cogsAmount',
              form.kind === 'sale'
                ? 'Product cost (optional)'
                : 'Product cost returned (otherwise 0)',
              'number'
            )}
            {form.kind === 'chargeback' && (
              <S.SelectLabel>
                Outcome
                <select
                  disabled={caseOutcome}
                  value={form.chargebackOutcome || 'open'}
                  onChange={(e) => change('chargebackOutcome', e.target.value)}
                >
                  {['open', 'won', 'lost'].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </S.SelectLabel>
            )}
          </S.Fields>
          {taxDiffers && (
            <label>
              <input
                type="checkbox"
                checked={!!form.taxOverride}
                onChange={(e) => change('taxOverride', e.target.checked)}
              />{' '}
              Tax differs from the event rate ({event.tax || 0}%); this amount
              is right (expected {formatPrice(Number(expectedTax))})
            </label>
          )}
          {outsideDays > 1 && (
            <label>
              <input
                type="checkbox"
                checked={!!form.dateOverride}
                onChange={(e) => change('dateOverride', e.target.checked)}
              />{' '}
              This date is outside the event dates; this date is right
            </label>
          )}
          {dateError && <S.ErrorText>{dateError}</S.ErrorText>}
          {caseOutcome && (
            <p>The outcome follows the referenced Fiserv case when saved.</p>
          )}
          {card && (
            <>
              <S.Fields>{field('settlementKey', 'Settlement key')}</S.Fields>
              <S.Fields>
                {(
                  [
                    'batchId',
                    'fundingId',
                    'depositDate',
                    'depositAmount',
                    'bankRowRef',
                    'qboTxnId',
                  ] as const
                ).map((key) => (
                  <Input
                    key={key}
                    name={`missing-ref-${key}`}
                    value={form.processorRefs?.[key] || ''}
                    type={key === 'depositDate' ? 'date' : 'text'}
                    onChange={(e) =>
                      change('processorRefs', {
                        ...form.processorRefs,
                        [key]: e.target.value,
                      })
                    }
                    label={
                      {
                        batchId: 'Batch ID',
                        fundingId: 'Funding ID',
                        depositDate: 'Deposit date',
                        depositAmount: 'Deposit amount',
                        bankRowRef: 'Bank row reference',
                        qboTxnId: 'QuickBooks transaction ID',
                      }[key]
                    }
                  />
                ))}
              </S.Fields>
              <h4>Card transactions</h4>
              {cards.map((c, i) => (
                <S.Card key={i}>
                  <legend>Transaction {i + 1}</legend>
                  <S.Fields>
                    <Input
                      value={c.authCode}
                      label="Authorization code"
                      name={`missing-card-${i}-auth`}
                      aria-label={`Transaction ${i + 1} Authorization code`}
                      onChange={(e) =>
                        changeCard(i, 'authCode', e.target.value)
                      }
                    />
                    <Input
                      maxLength={4}
                      label="Last 4"
                      value={c.last4}
                      name={`missing-card-${i}-last4`}
                      aria-label={`Transaction ${i + 1} Last 4`}
                      onChange={(e) => changeCard(i, 'last4', e.target.value)}
                    />
                    <Input
                      type="number"
                      name={`missing-card-${i}-amount`}
                      label="Amount (incl. tip and fee)"
                      value={String(c.amountCents / 100)}
                      aria-label={`Transaction ${i + 1} Amount (incl. tip and fee)`}
                      onChange={(e) =>
                        changeCard(
                          i,
                          'amountCents',
                          Math.round(Number(e.target.value) * 100)
                        )
                      }
                    />
                    <Input
                      label="Tip"
                      type="number"
                      name={`missing-card-${i}-tip`}
                      value={String(c.tipCents / 100)}
                      aria-label={`Transaction ${i + 1} Tip`}
                      onChange={(e) =>
                        changeCard(
                          i,
                          'tipCents',
                          Math.round(Number(e.target.value) * 100)
                        )
                      }
                    />
                    <Input
                      type="date"
                      label="Business date"
                      value={c.businessDate}
                      name={`missing-card-${i}-date`}
                      aria-label={`Transaction ${i + 1} Business date`}
                      onChange={(e) =>
                        changeCard(i, 'businessDate', e.target.value)
                      }
                    />
                    <Input
                      name={`missing-card-${i}-processorId`}
                      label="Processor transaction ID (optional)"
                      value={c.cloverPaymentId || c.paTransactionId || ''}
                      aria-label={`Transaction ${i + 1} Processor transaction ID (optional)`}
                      onChange={(e) =>
                        changeCard(
                          i,
                          form.tender === 'clover_card'
                            ? 'cloverPaymentId'
                            : 'paTransactionId',
                          e.target.value
                        )
                      }
                    />
                  </S.Fields>
                  <S.Actions>
                    <Button
                      text="Remove transaction"
                      onClick={() =>
                        setCards(cards.filter((_, index) => index !== i))
                      }
                    />
                  </S.Actions>
                </S.Card>
              ))}
              <Button
                text="Add card transaction"
                onClick={() =>
                  setCards([
                    ...cards,
                    {
                      last4: '',
                      tipCents: 0,
                      authCode: '',
                      amountCents: 0,
                      businessDate: form.saleDate || '',
                    },
                  ])
                }
              />
            </>
          )}
          {field('sourceRef', 'Source reference')}
          {field('note', 'Note')}
          <strong>Total: {formatPrice(totalAmount(form))}</strong>
        </>
      )}
      {field(
        'reason',
        remove
          ? 'Reason for deletion'
          : row
            ? 'Reason for change'
            : 'Reason / source explanation'
      )}
      {error && <S.ErrorText role="alert">{error}</S.ErrorText>}
      {duplicateId && onDuplicate && (
        <Button
          text="Open existing row"
          onClick={() => onDuplicate(duplicateId)}
        />
      )}
      {conflict && (
        <p>
          The list has refreshed. Close this dialog and reopen the current row
          before saving.
        </p>
      )}
      <S.Actions>
        <Button text="Cancel" onClick={onClose} />
        <Button
          disabled={conflict}
          type={ButtonTypes.Submit}
          loading={saving || deleting}
          text={
            remove
              ? 'Delete'
              : row
                ? 'Save changes'
                : kind === 'sale'
                  ? 'Add missing sale'
                  : `Add ${kind}`
          }
        />
      </S.Actions>
    </S.Form>
  );
};
